import { useEffect, useState } from 'react';
import { useIntl } from '@nile/components';
import { AccountClient } from '@nile/graphql';
import gql from 'graphql-tag';
import { ALL_ENTITIES } from './constant';

const getMeta = (client = AccountClient) => {
  return client.query({
    query: gql`
      query {
        getEntitiesMeta(entityNames : ${JSON.stringify(ALL_ENTITIES)}){
          label
          name
          attributes {
            name
            label
            attributePlaceHolder {
              key
              label
            }
            filterType
            dataType
            entity
            uiDataType
            conditionPlaceHolders {
              key
              label
            }
            isCustom
            autoComplete
            useInMetric
            dataReportRenderType
            useInGroupBy
            useInCondition
          }
        }
      }
    `,
    variables: {
      listInfo: {}
    },
    fetchPolicy: 'network-only'
  });
};

const useEntityMeta = (props = {}) => {
  const { entities = null, getRelatedEntities = true } = props;

  const intl = useIntl();

  const [meta, setMeta] = useState([]);

  const [attribute, setAttribute] = useState({
    groupByAttributes: [],
    groupAttributes: [],
    metricAttributes: []
  });

  const [loading, setLoading] = useState(false);

  const [entityCount, setEntityCount] = useState(
    (entities && entities.length) || 1
  );

  const entitiesName = entities || ALL_ENTITIES;

  const callGetMeta = async (entity) => {
    setLoading(true);
    const metaRes = await AccountClient.query({
      query: gql`
        query {
          getEntitiesMeta(entityNames : ${JSON.stringify(entity)}){
            label
            name
            attributes {
              name
              label
              attributePlaceHolder {
                key
                label
              }
              filterType
              metricUnits
              defaultMetricUnit
              dataType
              entity
              uiDataType
              conditionPlaceHolders {
                key
                label
              }
              isCustom
              autoComplete
              useInMetric
              metricFunctions
              labelInMetric
              useInGroupBy
              useInCondition
              conditionPlaceHoldersMeta
            }
          }
        }
      `,
      variables: {
        listInfo: {}
      },
      fetchPolicy: 'network-only'
    });

    return metaRes;
  };

  const getGlobalEntityAttributes = async () => {
    if (!meta) {
      return [];
    }

    const customAttributes = meta || [];

    const getEntityAttribute = (ent) =>
      customAttributes.find((item) => {
        return item.name === ent;
      })?.attributes || [];

    const getAllFilteredAttributes = (ent, type) => {
      return getEntityAttribute(ent)?.filter((attr) => attr[type]);
    };

    const getAttributes = (item) => {
      if (item.dataType !== 'entity') {
        return item;
      }
      return getAllFilteredAttributes(item.entity, 'useInGroupBy')
        .map((at) => {
          if (at.dataType === 'entity') {
            return getAttributes(at).map((subItem) => {
              return {
                ...subItem,
                name: `${item.name}.${subItem.name}`
              };
            });
          }
          return {
            ...at,
            name: `${item.name}.${at.name}`
          };
        })
        .flat();
    };

    const groupByAttributes = entities
      .map((el) => {
        return getAllFilteredAttributes(el, 'useInGroupBy')
          .map((attr) => getAttributes(attr))
          .flat();
      })
      .flat();

    const metricAttributes = entities
      .map((el) => getAllFilteredAttributes(el, 'useInMetric'))
      .map((attr) =>
        attr.dataType === 'entity'
          ? getAllFilteredAttributes(attr.entity, 'useInMetric').map((at) => ({
              ...at,
              name: `${attr.name}.${at.name}`
            }))
          : attr
      )
      .flat();

    setAttribute({
      groupByAttributes: groupByAttributes,
      metricAttributes: metricAttributes
    });
    if (entityCount === meta.length) {
      setLoading(false);
    }
    return { groupByAttributes, metricAttributes };
  };

  const checkRelatedEntities = async (metas) => {
    if (!metas || !getRelatedEntities) {
      return [];
    }
    const customAttributes = metas || [];

    const getEntityAttribute = (ent) =>
      customAttributes.find((item) => {
        return item.name === ent;
      })?.attributes || [];

    const getAllFilteredAttributes = (ent, type) => {
      return getEntityAttribute(ent)?.filter((attr) => attr[type]);
    };

    const getAllRelatedFilteredAttributes = async (ele) => {
      const groupattr = getAllFilteredAttributes(ele, 'useInGroupBy');

      const relatedEnt = groupattr
        .filter((el) => el.dataType === 'entity')
        .map((ent) => ent.entity);

      const relatedEntity = [...new Set(relatedEnt)];

      if (relatedEntity.length) {
        setEntityCount((prevState) => prevState + relatedEntity.length);
        await callGetMeta(relatedEntity)
          .then(({ data }) => {
            setMeta((prevData) => [...prevData, ...data?.getEntitiesMeta]);
            checkRelatedEntities(data?.getEntitiesMeta);
          })
          .catch((err) => console.log('err', err));
      }
    };

    const metacompleted = entities.map((el) =>
      getAllRelatedFilteredAttributes(el)
    );
    return metacompleted;
  };

  const getData = async (data) => {
    setMeta(data?.getEntitiesMeta);
    await checkRelatedEntities(data?.getEntitiesMeta);
  };

  useEffect(() => {
    if (entities.length) {
      setEntityCount(entities.length);
      const resData = callGetMeta(entitiesName);
      resData
        .then(({ data }) => {
          getData(data);
        })
        .catch((err) => console.log('err', err));
    }
  }, [JSON.stringify(entities)]);

  useEffect(() => {
    if (meta.length) {
      getGlobalEntityAttributes();
    }
  }, [meta]);

  return { loading, meta, attribute };
};

export { useEntityMeta, getMeta };
